import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  CircularProgress,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  FilterAlt as FilterIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import { format, addMonths, parseISO, isValid } from 'date-fns';
import { db } from '../../../firebase/config';
import { collection, addDoc, query, orderBy, limit, getDocs, Timestamp, deleteDoc, doc } from 'firebase/firestore';

type SearchHistory = {
  id: string;
  query: string;
  timestamp: Timestamp;
  results: SearchResult[];
  resultCount: number;
  status: 'completed' | 'failed';
  error?: string;
  filters?: SearchFilters;
};

type SearchFilters = {
  startDate: Date | null;
  endDate: Date | null;
  city: string | null;
};

type SearchResult = {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  source: string;
  url: string;
  relevanceScore: number;
  saved?: boolean;
};

const AISearchConsole: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<SearchHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    startDate: null,
    endDate: null,
    city: null
  });
  
  // Popular cities for events
  const popularCities = [
    "New York", "Los Angeles", "Chicago", "San Francisco", "Miami", 
    "Austin", "Seattle", "Boston", "Las Vegas", "Nashville",
    "Denver", "Atlanta", "Portland", "Washington DC", "San Diego",
    "New Orleans", "Philadelphia", "Dallas", "Houston", "Phoenix"
  ];
  
  useEffect(() => {
    fetchSearchHistory();
  }, []);
  
  const fetchSearchHistory = async () => {
    try {
      setHistoryLoading(true);
      
      // In a real implementation, you would fetch from Firestore
      // For now, using mock data
      const mockHistory: SearchHistory[] = [
        {
          id: 'search1',
          query: 'tech conferences in San Francisco next month',
          timestamp: Timestamp.fromDate(new Date(Date.now() - 2 * 24 * 3600 * 1000)),
          results: [
            {
              id: 'result1',
              title: 'DevWorld Conference 2025',
              description: 'The largest developer conference in the Bay Area',
              date: '2025-06-15',
              time: '09:00 - 18:00',
              location: 'Moscone Center, San Francisco',
              source: 'Eventbrite',
              url: 'https://example.com/events/devworld-2025',
              relevanceScore: 0.95,
              saved: true
            },
            {
              id: 'result2',
              title: 'AI Summit San Francisco',
              description: 'Exploring the future of artificial intelligence',
              date: '2025-06-22',
              time: '10:00 - 17:00',
              location: 'Marriott Marquis, San Francisco',
              source: 'Meetup',
              url: 'https://example.com/events/ai-summit-sf',
              relevanceScore: 0.87
            }
          ],
          resultCount: 2,
          status: 'completed'
        },
        {
          id: 'search2',
          query: 'music festivals in California summer 2025',
          timestamp: Timestamp.fromDate(new Date(Date.now() - 5 * 24 * 3600 * 1000)),
          results: [
            {
              id: 'result3',
              title: 'California Summer Fest 2025',
              description: 'Three days of music, art, and culture',
              date: '2025-07-10',
              time: '12:00 - 23:00',
              location: 'Golden Gate Park, San Francisco',
              source: 'Eventbrite',
              url: 'https://example.com/events/summer-fest-2025',
              relevanceScore: 0.92,
              saved: true
            },
            {
              id: 'result4',
              title: 'SoCal Music Weekend',
              description: 'The biggest bands play Southern California',
              date: '2025-07-25',
              time: '14:00 - 23:00',
              location: 'Venice Beach, Los Angeles',
              source: 'Facebook Events',
              url: 'https://example.com/events/socal-music-weekend',
              relevanceScore: 0.84
            },
            {
              id: 'result5',
              title: 'Indie Music Festival',
              description: 'Celebrating independent artists',
              date: '2025-08-05',
              time: '16:00 - 22:00',
              location: 'San Diego Convention Center',
              source: 'Meetup',
              url: 'https://example.com/events/indie-music-fest',
              relevanceScore: 0.79
            }
          ],
          resultCount: 3,
          status: 'completed'
        },
        {
          id: 'search3',
          query: 'invalid api query example',
          timestamp: Timestamp.fromDate(new Date(Date.now() - 1 * 24 * 3600 * 1000)),
          results: [],
          resultCount: 0,
          status: 'failed',
          error: 'API Error: Could not process natural language query'
        }
      ];
      
      setSearchHistory(mockHistory);
      setHistoryLoading(false);
    } catch (err) {
      console.error('Error fetching search history:', err);
      setError('Failed to load search history');
      setHistoryLoading(false);
    }
  };
  
  const handleSearch = async () => {
    if (!searchQuery.trim() && !filters.city && !filters.startDate && !filters.endDate) {
      setError('Please enter a search query or select at least one filter');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSearchResults([]);
      
      // In a real implementation, this would call the Perplexity API
      // For now, we'll simulate a response
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate mock results based on the query and filters
      let mockResults: SearchResult[] = [
        {
          id: `result-${Date.now()}-1`,
          title: `${searchQuery.slice(0, 15) || "Event"}... 2025`,
          description: `This is a sample event related to "${searchQuery || "your search"}"`,
          date: '2025-06-15',
          time: '09:00 - 18:00',
          location: filters.city || 'San Francisco, CA',
          source: 'Perplexity AI Search',
          url: 'https://example.com/events/sample-1',
          relevanceScore: 0.95
        },
        {
          id: `result-${Date.now()}-2`,
          title: `Another ${searchQuery.slice(0, 10) || "Topic"}... Conference`,
          description: `A second event about "${searchQuery || "your search"}" with different details`,
          date: '2025-07-22',
          time: '10:00 - 17:00',
          location: filters.city || 'New York, NY',
          source: 'Perplexity AI Search',
          url: 'https://example.com/events/sample-2',
          relevanceScore: 0.82
        },
        {
          id: `result-${Date.now()}-3`,
          title: `${searchQuery.slice(0, 5) || "Tech"}... Workshop`,
          description: `A workshop focused on "${searchQuery || "technology"}" for beginners`,
          date: '2025-08-10',
          time: '13:00 - 16:00',
          location: 'Online',
          source: 'Perplexity AI Search',
          url: 'https://example.com/events/sample-3',
          relevanceScore: 0.78
        }
      ];
      
      // Filter results by date if date filters are applied
      if (filters.startDate || filters.endDate) {
        mockResults = mockResults.filter(result => {
          const eventDate = new Date(result.date);
          if (filters.startDate && eventDate < filters.startDate) return false;
          if (filters.endDate && eventDate > filters.endDate) return false;
          return true;
        });
      }
      
      // Filter results by city if city filter is applied
      if (filters.city) {
        mockResults = mockResults.filter(result => 
          result.location.toLowerCase().includes(filters.city!.toLowerCase())
        );
      }
      
      setSearchResults(mockResults);
      
      // Save to search history
      const newHistoryItem: SearchHistory = {
        id: `search-${Date.now()}`,
        query: searchQuery,
        timestamp: Timestamp.fromDate(new Date()),
        results: mockResults,
        resultCount: mockResults.length,
        status: 'completed',
        filters: { ...filters }
      };
      
      // In a real implementation, you would save to Firestore
      // For now, just update the local state
      setSearchHistory(prev => [newHistoryItem, ...prev]);
      
      setLoading(false);
    } catch (err) {
      console.error('Error performing AI search:', err);
      setError('Failed to perform search. Please try again.');
      setLoading(false);
      
      // Save failed search to history
      const failedHistoryItem: SearchHistory = {
        id: `search-${Date.now()}`,
        query: searchQuery,
        timestamp: Timestamp.fromDate(new Date()),
        results: [],
        resultCount: 0,
        status: 'failed',
        error: 'API Error: Failed to retrieve results'
      };
      
      setSearchHistory(prev => [failedHistoryItem, ...prev]);
    }
  };
  
  const handleSaveResult = (resultId: string) => {
    // In a real implementation, you would save this to your events database
    setSearchResults(prev => 
      prev.map(result => 
        result.id === resultId ? { ...result, saved: true } : result
      )
    );
    
    // Also update in history if it exists there
    setSearchHistory(prev => 
      prev.map(history => ({
        ...history,
        results: history.results.map(result => 
          result.id === resultId ? { ...result, saved: true } : result
        )
      }))
    );
    
    alert('Event saved to database!');
  };
  
  const handleDeleteHistory = (historyId: string) => {
    if (window.confirm('Are you sure you want to delete this search from history?')) {
      // In a real implementation, you would delete from Firestore
      // For now, just update the local state
      setSearchHistory(prev => prev.filter(item => item.id !== historyId));
      
      if (selectedHistoryItem?.id === historyId) {
        setOpenHistoryDialog(false);
        setSelectedHistoryItem(null);
      }
    }
  };
  
  const handleDeleteHistoryItem = handleDeleteHistory;
  
  const handleViewHistory = (historyItem: SearchHistory) => {
    setSelectedHistoryItem(historyItem);
    setOpenHistoryDialog(true);
  };
  
  const handleRerunSearch = (query: string) => {
    setSearchQuery(query);
    setOpenHistoryDialog(false);
    setTimeout(() => {
      handleSearch();
    }, 100);
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        AI Search Console
      </Typography>
      <Typography variant="body1" paragraph>
        Use natural language to search for events from external sources using Perplexity AI technology.
      </Typography>
      
      {/* Search Input */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Search for Events"
              placeholder="Try: 'tech conferences' or 'outdoor music festivals'"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
              helperText="Use natural language to describe the events you're looking for"
            />
          </Grid>
          
          {/* Toggle filters button */}
          <Grid item xs={12}>
            <Button 
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              color="secondary"
              sx={{ mb: 1 }}
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </Grid>
          
          {/* Advanced filters section */}
          {showFilters && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <FilterIcon color="primary" sx={{ mr: 1 }} /> Advanced Filters
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Date Range */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                      <DateRangeIcon fontSize="small" sx={{ mr: 1 }} /> Date Range
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          type="date"
                          label="Start Date"
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : ''}
                          onChange={(e) => {
                            const dateStr = e.target.value;
                            if (dateStr) {
                              try {
                                const newDate = parseISO(dateStr);
                                if (isValid(newDate)) {
                                  setFilters(prev => ({
                                    ...prev,
                                    startDate: newDate
                                  }));
                                }
                              } catch (error) {
                                console.error("Invalid date format", error);
                              }
                            } else {
                              setFilters(prev => ({
                                ...prev,
                                startDate: null
                              }));
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          type="date"
                          label="End Date"
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : ''}
                          onChange={(e) => {
                            const dateStr = e.target.value;
                            if (dateStr) {
                              try {
                                const newDate = parseISO(dateStr);
                                if (isValid(newDate)) {
                                  setFilters(prev => ({
                                    ...prev,
                                    endDate: newDate
                                  }));
                                }
                              } catch (error) {
                                console.error("Invalid date format", error);
                              }
                            } else {
                              setFilters(prev => ({
                                ...prev,
                                endDate: null
                              }));
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  
                  {/* City Selection */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                      <LocationIcon fontSize="small" sx={{ mr: 1 }} /> Location
                    </Typography>
                    <Autocomplete
                      options={popularCities}
                      freeSolo
                      value={filters.city}
                      onChange={(event, newValue) => {
                        setFilters(prev => ({ ...prev, city: newValue }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="City"
                          placeholder="Select or type a city"
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  
                  {/* Clear Filters */}
                  <Grid item xs={12}>
                    <Button 
                      size="small" 
                      onClick={() => setFilters({ startDate: null, endDate: null, city: null })}
                      sx={{ mt: 1 }}
                    >
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
          
          <Grid item xs={12} sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading || (!searchQuery.trim() && !filters.city && !filters.startDate && !filters.endDate)}
            >
              Search with AI
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setOpenHistoryDialog(true)}
              disabled={searchHistory.length === 0}
            >
              Search History
            </Button>
          </Grid>
          
          {/* Active Filters Display */}
          {(filters.startDate || filters.endDate || filters.city) && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {filters.startDate && (
                  <Chip 
                    icon={<EventIcon />} 
                    label={`From: ${format(filters.startDate, 'MMM d, yyyy')}`} 
                    color="primary" 
                    variant="outlined"
                    onDelete={() => setFilters(prev => ({ 
                      ...prev, 
                      startDate: null 
                    }))}
                  />
                )}
                
                {filters.endDate && (
                  <Chip 
                    icon={<EventIcon />} 
                    label={`To: ${format(filters.endDate, 'MMM d, yyyy')}`} 
                    color="primary" 
                    variant="outlined"
                    onDelete={() => setFilters(prev => ({ 
                      ...prev, 
                      endDate: null 
                    }))}
                  />
                )}
                
                {filters.city && (
                  <Chip 
                    icon={<LocationIcon />} 
                    label={`Location: ${filters.city}`} 
                    color="primary" 
                    variant="outlined"
                    onDelete={() => setFilters(prev => ({ 
                      ...prev, 
                      city: null 
                    }))}
                  />
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Search Results */}
      {searchResults.length > 0 && !loading && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Search Results for "{searchQuery}"
          </Typography>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Relevance</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((result) => (
                    <TableRow key={result.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {result.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {result.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Box>
                            {result.date}
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {result.time}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                          {result.location}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={result.source} />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={`${Math.round(result.relevanceScore * 100)}%`} 
                          color={
                            result.relevanceScore > 0.9 ? 'success' :
                            result.relevanceScore > 0.7 ? 'primary' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        {!result.saved ? (
                          <Tooltip title="Save to Event Database">
                            <IconButton 
                              color="primary" 
                              onClick={() => handleSaveResult(result.id)}
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Already Saved">
                            <span>
                              <IconButton disabled>
                                <SaveIcon color="disabled" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="View Source">
                          <IconButton 
                            href={result.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
      
      {/* Recent Searches Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Recent Searches
        </Typography>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : searchHistory.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No search history yet. Try searching for events using the AI search above.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {searchHistory.slice(0, 3).map((item) => (
              <Grid item xs={12} md={4} key={item.id}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" noWrap>
                      "{item.query}"
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(item.timestamp.seconds * 1000).toLocaleString()}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        size="small" 
                        label={item.status === 'completed' ? `${item.resultCount} results` : 'Failed'} 
                        color={item.status === 'completed' ? 'success' : 'error'} 
                      />
                      {item.status === 'failed' && (
                        <Typography variant="caption" color="error">
                          {item.error}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      startIcon={<HistoryIcon />}
                      onClick={() => handleViewHistory(item)}
                    >
                      View Details
                    </Button>
                    <Button 
                      size="small" 
                      startIcon={<SearchIcon />}
                      onClick={() => handleRerunSearch(item.query)}
                      disabled={loading}
                    >
                      Search Again
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      
      {/* Search History Dialog */}
      <Dialog 
        open={openHistoryDialog} 
        onClose={() => setOpenHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1 }} />
            Search History
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : searchHistory.length === 0 ? (
            <Alert severity="info">No search history found</Alert>
          ) : (
            <List>
              {searchHistory.map((item) => (
                <ListItem
                  key={item.id}
                  alignItems="flex-start"
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        onClick={() => handleRerunSearch(item.query)}
                        title="Re-run this search"
                      >
                        <SearchIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteHistoryItem(item.id)}
                        title="Delete from history"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1">
                          {item.query || "Search"}
                        </Typography>
                        <Chip 
                          size="small" 
                          label={`${item.resultCount} results`}
                          color={item.status === 'completed' ? 'success' : 'error'}
                        />
                        {item.filters?.city && (
                          <Chip 
                            icon={<LocationIcon fontSize="small" />} 
                            size="small" 
                            label={item.filters.city}
                            variant="outlined" 
                          />
                        )}
                        {item.filters?.startDate && (
                          <Chip 
                            icon={<DateRangeIcon fontSize="small" />} 
                            size="small" 
                            label={`From: ${
                              // Safely format the date by converting to string first
                              format(new Date(String(item.filters.startDate)), 'MM/dd/yyyy')
                            }`}
                            variant="outlined" 
                          />
                        )}
                        {item.filters?.endDate && (
                          <Chip 
                            icon={<DateRangeIcon fontSize="small" />} 
                            size="small" 
                            label={`To: ${
                              // Safely format the date by converting to string first
                              format(new Date(String(item.filters.endDate)), 'MM/dd/yyyy')
                            }`}
                            variant="outlined" 
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary">
                          {item.timestamp.toDate().toLocaleString()}
                        </Typography>
                        {item.error && (
                          <Typography variant="body2" color="error">
                            {item.error}
                          </Typography>
                        )}
                      </>
                    }
                    onClick={() => handleViewHistory(item)}
                    sx={{ cursor: 'pointer' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AISearchConsole;
